import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import type { ComboboxOption } from '../../components/Combobox';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useArticles } from './articles/queries';
import { useMergeTicket, useSplitTicket } from './queries';
import { TicketCombobox } from './TicketCombobox';

interface MergeSplitDialogProps {
  ticketId: string;
}

/**
 * Zusammenfuehren und Aufteilen -- F1-13, F1-14, K-14, K-15.
 *
 * **Beides in einem Dialog**, weil beides dieselbe Frage beantwortet: Dieser
 * Vorgang hat den falschen Zuschnitt. Zwei Knoepfe nebeneinander in der
 * Kopfleiste waeren zwei selten gebrauchte Knoepfe an einer Stelle, an der
 * jeder Platz von der taeglichen Arbeit gebraucht wird.
 *
 * **Beide fragen nach.** Zusammenfuehren schliesst den Quellvorgang,
 * Aufteilen verschiebt Beitraege -- beides laesst sich nicht mit einem Klick
 * rueckgaengig machen. Die Rueckfrage ist hier keine Hoeflichkeit, sondern
 * die letzte Stelle, an der sich ein Versehen aufhalten laesst.
 *
 * **Der Quellvorgang bleibt beim Zusammenfuehren bestehen.** Seine Nummer
 * steht in der Kundenkommunikation und muss auffindbar bleiben; der Server
 * schliesst ihn und verknuepft beide. Die Maske sagt das, damit niemand ein
 * Loeschen erwartet.
 */
export function MergeSplitDialog({ ticketId }: MergeSplitDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<'closed' | 'merge' | 'split'>('closed');
  const [target, setTarget] = useState<ComboboxOption | null>(null);
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const merge = useMergeTicket(ticketId);
  const split = useSplitTicket(ticketId);
  const articles = useArticles(ticketId);

  const close = () => {
    setMode('closed');
    setTarget(null);
    setTitle('');
    setSelected([]);
  };

  if (mode === 'closed') {
    return (
      <Button
        variant="ghost"
        onClick={() => {
          setMode('merge');
        }}
      >
        {t('tickets.reshape')}
      </Button>
    );
  }

  return (
    <div className="notice notice--warning" role="alertdialog" aria-live="assertive">
      <div className="row" role="group" aria-label={t('tickets.reshape')}>
        <Button
          variant={mode === 'merge' ? 'secondary' : 'ghost'}
          aria-pressed={mode === 'merge'}
          onClick={() => {
            setMode('merge');
          }}
        >
          {t('tickets.merge')}
        </Button>
        <Button
          variant={mode === 'split' ? 'secondary' : 'ghost'}
          aria-pressed={mode === 'split'}
          onClick={() => {
            setMode('split');
          }}
        >
          {t('tickets.split')}
        </Button>
      </div>

      {mode === 'merge' ? (
        <>
          <p>{t('tickets.mergeHint')}</p>
          <div className="field">
            <label className="field__label" htmlFor="merge-target">
              {t('tickets.mergeTarget')}
            </label>
            <TicketCombobox
              id="merge-target"
              value={target}
              onChange={setTarget}
              excludeId={ticketId}
              placeholder={t('tickets.mergeTargetHint')}
            />
          </div>

          {merge.isError ? <ErrorNotice error={merge.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={target === null}
              busy={merge.isPending}
              onClick={() => {
                if (target === null) return;
                merge.mutate(target.id, {
                  onSuccess: (target_ticket) => {
                    close();
                    void navigate({
                      to: '/app/tickets/$ticketId',
                      params: { ticketId: target_ticket.id },
                    });
                  },
                });
              }}
            >
              {t('tickets.mergeConfirm')}
            </Button>
            <Button variant="ghost" onClick={close}>
              {t('app.cancel')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p>{t('tickets.splitHint')}</p>
          <div className="field">
            <label className="field__label" htmlFor="split-title">
              {t('tickets.splitTitle')}
            </label>
            <input
              id="split-title"
              className="field__control"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
              }}
            />
          </div>

          {(articles.data ?? []).map((article) => (
            <label className="field__checkbox" key={article.id}>
              <input
                type="checkbox"
                checked={selected.includes(article.id)}
                onChange={(event) => {
                  setSelected((current) =>
                    event.target.checked
                      ? [...current, article.id]
                      : current.filter((value) => value !== article.id),
                  );
                }}
              />
              {(article.body_text || '').slice(0, 80) || t('articles.noText')}
            </label>
          ))}

          {split.isError ? <ErrorNotice error={split.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={title.trim() === '' || selected.length === 0}
              busy={split.isPending}
              onClick={() => {
                split.mutate(
                  { title: title.trim(), article_ids: selected },
                  {
                    onSuccess: (created) => {
                      close();
                      void navigate({
                        to: '/app/tickets/$ticketId',
                        params: { ticketId: created.id },
                      });
                    },
                  },
                );
              }}
            >
              {t('tickets.splitConfirm')}
            </Button>
            <Button variant="ghost" onClick={close}>
              {t('app.cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
